import * as React from 'react'
import { Link } from 'react-router-dom'

import { Site } from '@envato/foundation-types'

import * as styles from './categoryTilesBlock.module.css'

import { reportBlockClick } from '../../../../common/analytics/homePageAnalytics'
import { config } from '../../../../common/config'
import { ICommonLocation } from '../../../../common/utils/location/types'
import ui from '../../../../common/constants/ui'
import NextGenImageWrapper from '../../nextGenImageWrapper'
import SearchLink from '../../../containers/searchLinkContainer'
import { IHomePageBlockComponent, ICta } from '../../../../typings/pageBlocks'
import { ILocationParams } from '../../../../common/reducers/reactRouterLocation'
import { IExperimentsState } from '../../../../common/reducers/experiments'
import Button from '../../button'
import SVGSpriteWrapper from '../../svgSpriteWrapper'

import EnhancedLazyLoad from '../../../components/enhancedLazyLoad'
import ImagePlaceholder from '../../../components/imagePlaceholder'

interface ITile {
  title: string
  subtitle: string
  category: string
}

interface IProps extends IHomePageBlockComponent {
  currentSite: Site
  location: ICommonLocation
  experiments: IExperimentsState
  data: ITile[]
  cta: ICta
}

const colors = [
  '#d5d5ff',
  '#ffd6b0',
  '#beebf6',
  '#fff48a',
  '#d2fae6',
  '#d5d5ff'
]

const placeholderRatio = 300 / 590

const getTileReporter = (item: ITile) => () =>
  reportBlockClick('Category Tiles', {
    eventLabel: item.category,
    dimension19: 'body',
    eventAction: 'click;tiles'
  })

const renderSVGForCategory = (item: ITile, index: number, site: Site) => (
  <SVGSpriteWrapper.SVG
    size="large"
    iconId={`#${site}-svg-${item.category}`}
    fallbackId={`#${site}-svg-generic`}
    backgroundColor={colors[index]}
  />
)

const renderCategoryTextAndLinks = (
  item: ITile,
  locationParams: ILocationParams
) => {
  return (
    <React.Fragment>
      <h2>
        <SearchLink
          locationParams={locationParams}
          className={styles.tileTitle}
          title={`View ${item.title}`}
          onClick={getTileReporter(item)}
        >
          {item.title}
        </SearchLink>
      </h2>

      <p className={styles.tileSubtitle}>{item.subtitle}</p>

      <React.Fragment>
        <SearchLink
          locationParams={{ ...locationParams, sort: 'date' }}
          className={styles.tileLink}
          title={`Newest ${item.title}`}
          onClick={getTileReporter(item)}
        >
          Newest
        </SearchLink>

        <SearchLink
          locationParams={{ ...locationParams, sort: 'sales' }}
          className={styles.tileLink}
          title={`Bestselling ${item.title}`}
          onClick={getTileReporter(item)}
        >
          Bestsellers
        </SearchLink>
      </React.Fragment>
    </React.Fragment>
  )
}

const renderTileWithoutImage = (site: Site) => (item: ITile, index: number) => {
  const locationParams: ILocationParams = {
    category: item.category
  }

  return (
    <div key={index} className={styles.smallTile}>
      <SearchLink locationParams={locationParams} title={`View ${item.title}`}>
        <div className={styles.tileIcon}>
          {renderSVGForCategory(item, index, site)}
        </div>
      </SearchLink>

      <div className={styles.tileDetail}>
        {renderCategoryTextAndLinks(item, locationParams)}
      </div>
    </div>
  )
}

const renderTileWithImage = (site: Site) => (item: ITile, index: number) => {
  const IMG_PATH = `${config.assets.PUBLIC_PATH}/images/home-page/new-design/${site}`
  const imageUrl = `${IMG_PATH}/category-tiles-${item.category}.webp`
  const locationParams: ILocationParams = {
    category: item.category
  }

  return (
    <div key={index} className={styles.tile}>
      <div className={styles.tileDetail}>
        {renderCategoryTextAndLinks(item, locationParams)}
      </div>
      <SearchLink
        className={styles.tileArt}
        locationParams={locationParams}
        title={`View ${item.title}`}
      >
        <EnhancedLazyLoad
          offset={ui.lazyLoadOffset}
          placeholder={
            <ImagePlaceholder
              ratio={placeholderRatio}
              className={styles.tileImage}
            />
          }
        >
          <NextGenImageWrapper className={styles.imageWrapper}>
            <img className={styles.tileImage} src={imageUrl} alt={item.title} />
          </NextGenImageWrapper>
        </EnhancedLazyLoad>

        <div className={styles.tileIconOverImage}>
          {renderSVGForCategory(item, index, site)}
        </div>
      </SearchLink>
    </div>
  )
}

const CategoryTilesBlock = ({ data, cta, currentSite }: IProps) => {
  const showImageInTile = ![Site.audiojungle, Site.threedocean].includes(
    currentSite
  )

  return (
    <section className={styles.root} data-test-selector="categoryTilesBlock">
      <div className={styles.wrapper}>
        <div className={showImageInTile ? styles.tiles : styles.smallTiles}>
          {showImageInTile
            ? data.map(renderTileWithImage(currentSite))
            : data.map(renderTileWithoutImage(currentSite))}
        </div>

        <Button
          type={Button.Type.Primary}
          className={styles.cta}
          to={cta.url}
          renderAs={Link}
          onClick={() =>
            reportBlockClick('Category Tiles', {
              eventLabel: cta.title,
              dimension19: 'body'
            })
          }
        >
          {cta.title}
        </Button>
      </div>
    </section>
  )
}

export default CategoryTilesBlock
